'use client';

import React, { useState, useEffect, useCallback, Component, ErrorInfo, ReactNode } from 'react';
import Link from 'next/link';
import {
  ChevronLeft, Bookmark, Copy, Share2, Trash2, Check, BookOpen, MessageCircle,
  Filter, Sparkles, Search, SortAsc, SortDesc, FolderPlus, Folder, FolderOpen,
  X, Plus, ChevronDown, ArrowUpDown,
} from 'lucide-react';
import BookBadge, { BookType } from '@/components/BookBadge';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

export const BOOKMARKS_KEY = 'imaan_mate_bookmarks';
export const AI_SAVED_KEY = 'saved_answers';
export const FOLDERS_KEY = 'imaan_mate_folders';
export const FOLDER_ITEMS_KEY = 'imaan_mate_folder_items';

// Normalize any book value (full name or key) to a valid BookType key
const BOOK_NAME_TO_TYPE: Record<string, BookType> = {
  'tafsir of quran': 'tafsir',
  'stories of prophets': 'prophets',
  'sahih bukhari': 'bukhari',
  'sahih muslim': 'muslim',
  tafsir: 'tafsir',
  prophets: 'prophets',
  bukhari: 'bukhari',
  muslim: 'muslim',
};

function normalizeBookType(book: string | null | undefined): BookType {
  if (!book) return 'tafsir'; // fallback key — BookBadge will show "Islamic Texts" for unknown
  const key = book.toLowerCase().trim();
  return BOOK_NAME_TO_TYPE[key] || 'tafsir';
}

// localStorage passage shape
interface LocalPassage {
  id: string;
  book: BookType;
  title: string;
  snippet: string;
  reference?: string;
  savedAt: number;
  source: string;
}

// localStorage AI answer shape
interface LocalAISavedItem {
  id: string;
  question: string;
  answer: string;
  date: string;
  source: string;
}

// Unified display item
interface DisplayItem {
  id: string;
  type: 'passage' | 'ai';
  book?: BookType;
  title?: string;
  snippet?: string;
  reference?: string;
  question?: string;
  answer?: string;
  savedAt: number;
  source: string;
}

// Collection folder
interface CollectionFolder {
  id: string;
  name: string;
  createdAt: number;
  color: string;
}

type SortOption = 'date-desc' | 'date-asc' | 'source-asc' | 'source-desc';
type TypeFilter = 'all' | 'passage' | 'ai';

const FOLDER_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#14b8a6',
];

const BOOK_FILTER_OPTIONS: { key: string; label: string; value: string }[] = [
  { key: 'filter-all', label: 'All Sources', value: 'all' },
  { key: 'filter-tafsir', label: 'Tafsir', value: 'tafsir' },
  { key: 'filter-prophets', label: 'Prophets', value: 'prophets' },
  { key: 'filter-bukhari', label: 'Bukhari', value: 'bukhari' },
  { key: 'filter-muslim', label: 'Muslim', value: 'muslim' },
];

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'date-desc', label: 'Newest first' },
  { value: 'date-asc', label: 'Oldest first' },
  { value: 'source-asc', label: 'Source A–Z' },
  { value: 'source-desc', label: 'Source Z–A' },
];

// ─── Folder helpers ───────────────────────────────────────────────────────────

function loadLocalFolders(): CollectionFolder[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(FOLDERS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function saveLocalFolders(folders: CollectionFolder[]) {
  try { localStorage.setItem(FOLDERS_KEY, JSON.stringify(folders)); } catch { /* noop */ }
}

function loadLocalFolderItems(): Record<string, string[]> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(FOLDER_ITEMS_KEY);
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch { return {}; }
}

function saveLocalFolderItems(map: Record<string, string[]>) {
  try { localStorage.setItem(FOLDER_ITEMS_KEY, JSON.stringify(map)); } catch { /* noop */ }
}

function loadLocalPassages(): LocalPassage[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(BOOKMARKS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item) => item && typeof item === 'object' && item.id);
  } catch (error) {
    console.error('Error loading saved passages:', error);
    return [];
  }
}

function saveLocalPassages(items: LocalPassage[]) {
  try { localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(items)); } catch { /* noop */ }
}

function loadLocalAISaved(): LocalAISavedItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(AI_SAVED_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item) => item && typeof item === 'object' && item.id);
  } catch (error) {
    console.error('Error loading saved AI answers:', error);
    return [];
  }
}

function saveLocalAISaved(items: LocalAISavedItem[]) {
  try { localStorage.setItem(AI_SAVED_KEY, JSON.stringify(items)); } catch { /* noop */ }
}

function safeDateToTimestamp(date: string | number | undefined | null): number {
  if (!date) return Date.now();
  try {
    const ts = typeof date === 'number' ? date : new Date(date).getTime();
    return isNaN(ts) ? Date.now() : ts;
  } catch { return Date.now(); }
}

function formatSavedAt(ts: number): string {
  try {
    const now = Date.now();
    const diff = now - ts;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

// ─── Error Boundary ───────────────────────────────────────────────────────────

interface ErrorBoundaryState { hasError: boolean; }

class SavedTabErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(_: Error): ErrorBoundaryState { return { hasError: true }; }
  componentDidCatch(error: Error, info: ErrorInfo) { console.error('SavedTab error boundary caught:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="max-w-lg mx-auto px-4">
          <div className="flex items-center gap-3 pt-12 pb-4">
            <Link href="/" className="p-2 rounded-xl hover:bg-muted transition-colors" aria-label="Back to home">
              <ChevronLeft size={20} className="text-muted-foreground" />
            </Link>
            <h1 className="text-lg font-bold text-foreground">Saved</h1>
          </div>
          <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-5" style={{ background: 'var(--muted)' }}>
              <Bookmark size={36} className="text-accent opacity-60" />
            </div>
            <h2 className="text-base font-bold text-foreground mb-2">No saved items yet</h2>
            <p className="text-sm text-muted-foreground max-w-xs leading-relaxed mb-6">
              Search for Islamic knowledge and tap the bookmark icon to save passages.
            </p>
            <Link href="/search-results-page" className="flex items-center gap-2 text-sm font-semibold text-accent-foreground bg-accent px-5 py-2.5 rounded-full transition-all duration-150 active:scale-95 hover:opacity-90">
              <BookOpen size={15} />
              Browse Islamic Texts
            </Link>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Add to Folder Modal ──────────────────────────────────────────────────────

function AddToFolderModal({
  item,
  folders,
  folderItems,
  onClose,
  onAddToFolder,
  onRemoveFromFolder,
  onCreateFolder,
}: {
  item: DisplayItem;
  folders: CollectionFolder[];
  folderItems: Record<string, string[]>;
  onClose: () => void;
  onAddToFolder: (folderId: string, itemId: string) => void;
  onRemoveFromFolder: (folderId: string, itemId: string) => void;
  onCreateFolder: (name: string, color: string) => void;
}) {
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedColor, setSelectedColor] = useState(FOLDER_COLORS[0]);
  const [showCreate, setShowCreate] = useState(false);

  const isInFolder = (folderId: string) => (folderItems[folderId] || []).includes(item.id);

  const handleCreate = () => {
    if (!newFolderName.trim()) return;
    onCreateFolder(newFolderName.trim(), selectedColor);
    setNewFolderName('');
    setShowCreate(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-sm rounded-2xl p-5 animate-fade-in-up"
        style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-foreground">Add to Collection</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <X size={16} className="text-muted-foreground" />
          </button>
        </div>

        {folders.length === 0 && !showCreate && (
          <p className="text-sm text-muted-foreground mb-4">No collections yet. Create one below.</p>
        )}

        {/* Folder list */}
        <div className="space-y-2 mb-3 max-h-52 overflow-y-auto">
          {folders.map((folder) => {
            const inFolder = isInFolder(folder.id);
            return (
              <button
                key={folder.id}
                onClick={() => inFolder ? onRemoveFromFolder(folder.id, item.id) : onAddToFolder(folder.id, item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all duration-150 text-left ${
                  inFolder
                    ? 'border-accent/50 bg-accent/10' :'border-border hover:border-accent/30 hover:bg-muted/50'
                }`}
              >
                <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: folder.color + '25' }}>
                  {inFolder
                    ? <FolderOpen size={14} style={{ color: folder.color }} />
                    : <Folder size={14} style={{ color: folder.color }} />}
                </div>
                <span className="text-sm font-medium text-foreground flex-1">{folder.name}</span>
                {inFolder && <Check size={13} className="text-accent shrink-0" />}
              </button>
            );
          })}
        </div>

        {/* Create new folder */}
        {showCreate ? (
          <div className="border border-border rounded-xl p-3 space-y-3">
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Collection name…"
              className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none border-b border-border pb-1"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground shrink-0">Color:</span>
              <div className="flex gap-1.5 flex-wrap">
                {FOLDER_COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setSelectedColor(c)}
                    className={`w-5 h-5 rounded-full transition-all duration-150 ${selectedColor === c ? 'ring-2 ring-offset-1 ring-offset-card ring-white/60 scale-110' : ''}`}
                    style={{ background: c }}
                  />
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowCreate(false)} className="flex-1 py-1.5 rounded-lg text-xs font-semibold text-muted-foreground hover:bg-muted transition-colors">
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!newFolderName.trim()}
                className="flex-1 py-1.5 rounded-lg text-xs font-semibold bg-accent text-accent-foreground disabled:opacity-40 transition-colors"
              >
                Create
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowCreate(true)}
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl border border-dashed border-border text-sm font-medium text-muted-foreground hover:border-accent/40 hover:text-foreground transition-all duration-150"
          >
            <Plus size={14} />
            New collection
          </button>
        )}
      </div>
    </div>
  );
}

// ─── AI Answer Card ───────────────────────────────────────────────────────────

function AIAnswerCard({
  item,
  onDelete,
  onAddToFolder,
  animationDelay,
}: {
  item: DisplayItem;
  onDelete: (id: string) => void;
  onAddToFolder: (item: DisplayItem) => void;
  animationDelay: number;
}) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const PREVIEW = 220;
  const answer = item.answer || '';
  const hasMore = answer.length > PREVIEW;

  const handleCopy = async () => {
    try {
      const text = `Q: ${item.question}\n\nA: ${answer}`;
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Could not copy text'); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-4 animate-fade-in-up" style={{ animationDelay: `${animationDelay}ms` }}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-accent/15 text-accent border border-accent/30">
            <Sparkles size={9} />
            AI Answer
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <span className="text-[10px] text-muted-foreground">{formatSavedAt(item.savedAt)}</span>
          <button
            onClick={() => onAddToFolder(item)}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-accent hover:bg-accent/10 transition-all duration-150 ml-1"
            aria-label="Add to collection"
          >
            <FolderPlus size={13} />
          </button>
          <button
            onClick={() => onDelete(item.id)}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-400/10 transition-all duration-150"
            aria-label="Delete saved answer"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {item.question && (
        <p className="text-xs font-semibold text-accent mb-2 leading-snug">Q: {item.question}</p>
      )}

      <p className="text-sm text-muted-foreground leading-relaxed">
        {expanded ? answer : answer.slice(0, PREVIEW)}
        {!expanded && hasMore && <span className="text-accent font-medium">…</span>}
      </p>
      {hasMore && (
        <button onClick={() => setExpanded((v) => !v)} className="mt-1.5 text-xs font-semibold text-accent hover:underline">
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}

      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground px-2.5 py-1.5 rounded-lg hover:bg-muted transition-all duration-150"
        >
          {copied ? <Check size={13} className="text-green-400" /> : <Copy size={13} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
        <div className="flex-1" />
        <Link
          href={`/ai-chat-page?q=${encodeURIComponent((item.question || '').slice(0, 100))}`}
          className="flex items-center gap-1.5 text-[11px] font-semibold text-accent-foreground bg-accent/20 border border-accent/30 hover:bg-accent hover:text-accent-foreground px-3 py-1.5 rounded-full transition-all duration-150 active:scale-95"
        >
          <MessageCircle size={13} />
          Ask again
        </Link>
      </div>
    </div>
  );
}

// ─── Passage Card ─────────────────────────────────────────────────────────────

function PassageCard({
  item,
  onDelete,
  onAddToFolder,
  animationDelay,
}: {
  item: DisplayItem;
  onDelete: (id: string) => void;
  onAddToFolder: (item: DisplayItem) => void;
  animationDelay: number;
}) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const PREVIEW = 220;
  const snippet = item.snippet || '';
  const hasMore = snippet.length > PREVIEW;
  const isSearchResult = item.source === 'Search';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.success('Passage copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Could not copy text'); }
  };

  const handleShare = async () => {
    const text = `${item.title ? item.title + '\n\n' : ''}${snippet}${item.reference ? '\n— ' + item.reference : ''}`;
    if (typeof navigator !== 'undefined' && navigator.share) {
      try { await navigator.share({ title: item.title || 'Islamic Passage', text }); } catch { /* cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(text);
        toast.success('Copied — share via WhatsApp or email');
      } catch { toast.error('Could not share'); }
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-4 animate-fade-in-up" style={{ animationDelay: `${animationDelay}ms` }}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex flex-wrap items-center gap-2">
          {isSearchResult && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/25">
              <Search size={9} />
              Search Result
            </span>
          )}
          {item.book && <BookBadge book={item.book} size="sm" />}
          {item.reference && <span className="text-[10px] text-muted-foreground font-mono">{item.reference}</span>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <span className="text-[10px] text-muted-foreground">{formatSavedAt(item.savedAt)}</span>
          <button
            onClick={() => onAddToFolder(item)}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-accent hover:bg-accent/10 transition-all duration-150 ml-1"
            aria-label="Add to collection"
          >
            <FolderPlus size={13} />
          </button>
          <button
            onClick={() => onDelete(item.id)}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-400/10 transition-all duration-150"
            aria-label="Delete bookmark"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {item.title && <p className="text-sm font-semibold text-foreground mb-2 leading-snug">{item.title}</p>}

      <p className="text-sm text-muted-foreground leading-relaxed">
        {expanded ? snippet : snippet.slice(0, PREVIEW)}
        {!expanded && hasMore && <span className="text-accent font-medium">…</span>}
      </p>
      {hasMore && (
        <button onClick={() => setExpanded((v) => !v)} className="mt-1.5 text-xs font-semibold text-accent hover:underline">
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}

      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground px-2.5 py-1.5 rounded-lg hover:bg-muted transition-all duration-150"
        >
          {copied ? <Check size={13} className="text-green-400" /> : <Copy size={13} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
        <button
          onClick={handleShare}
          className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground px-2.5 py-1.5 rounded-lg hover:bg-muted transition-all duration-150"
        >
          <Share2 size={13} />
          Share
        </button>
        <div className="flex-1" />
      </div>
    </div>
  );
}

function EmptyState({ hasFilter }: { hasFilter: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
      <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-5" style={{ background: 'var(--muted)' }}>
        <Bookmark size={36} className="text-accent opacity-60" />
      </div>
      <h2 className="text-base font-bold text-foreground mb-2">
        {hasFilter ? 'No items in this category' : 'No saved items yet'}
      </h2>
      <p className="text-sm text-muted-foreground max-w-xs leading-relaxed mb-6">
        {hasFilter
          ? 'Try selecting a different filter to see your saved items.'
          : 'Search for Islamic knowledge and tap the bookmark icon to save passages.'}
      </p>
      {!hasFilter && (
        <Link href="/search-results-page" className="flex items-center gap-2 text-sm font-semibold text-accent-foreground bg-accent px-5 py-2.5 rounded-full transition-all duration-150 active:scale-95 hover:opacity-90">
          <BookOpen size={15} />
          Browse Islamic Texts
        </Link>
      )}
    </div>
  );
}

// ─── Sort Dropdown ────────────────────────────────────────────────────────────

function SortDropdown({ value, onChange }: { value: SortOption; onChange: (v: SortOption) => void }) {
  const [open, setOpen] = useState(false);
  const current = SORT_OPTIONS.find((o) => o.value === value);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:border-accent/40 hover:text-foreground transition-all duration-150"
      >
        {value.startsWith('date') ? (value === 'date-desc' ? <SortDesc size={12} /> : <SortAsc size={12} />) : <ArrowUpDown size={12} />}
        {current?.label}
        <ChevronDown size={11} className={`transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 w-40 rounded-xl border border-border bg-card shadow-lg overflow-hidden">
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-xs font-medium transition-colors ${
                  value === opt.value ? 'text-accent bg-accent/10' : 'text-foreground hover:bg-muted'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Folders Panel ────────────────────────────────────────────────────────────

function FoldersPanel({
  folders,
  folderItems,
  activeFolderId,
  onSelectFolder,
  onDeleteFolder,
  onCreateFolder,
  totalCount,
}: {
  folders: CollectionFolder[];
  folderItems: Record<string, string[]>;
  activeFolderId: string | null;
  onSelectFolder: (id: string | null) => void;
  onDeleteFolder: (id: string) => void;
  onCreateFolder: (name: string, color: string) => void;
  totalCount: number;
}) {
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [selectedColor, setSelectedColor] = useState(FOLDER_COLORS[0]);

  const handleCreate = () => {
    if (!newName.trim()) return;
    onCreateFolder(newName.trim(), selectedColor);
    setNewName('');
    setShowCreate(false);
  };

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        <Folder size={13} className="text-muted-foreground" />
        <span className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Collections</span>
        <div className="flex-1" />
        <button
          onClick={() => setShowCreate((v) => !v)}
          className="flex items-center gap-1 text-xs font-semibold text-accent hover:opacity-80 transition-opacity"
        >
          <Plus size={12} />
          New
        </button>
      </div>

      {showCreate && (
        <div className="border border-border rounded-xl p-3 mb-2 space-y-2">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Collection name…"
            className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none border-b border-border pb-1"
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          />
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Color:</span>
            <div className="flex gap-1.5">
              {FOLDER_COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => setSelectedColor(c)}
                  className={`w-4 h-4 rounded-full transition-all duration-150 ${selectedColor === c ? 'ring-2 ring-offset-1 ring-offset-card ring-white/60 scale-110' : ''}`}
                  style={{ background: c }}
                />
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowCreate(false)} className="flex-1 py-1 rounded-lg text-xs font-semibold text-muted-foreground hover:bg-muted transition-colors">Cancel</button>
            <button onClick={handleCreate} disabled={!newName.trim()} className="flex-1 py-1 rounded-lg text-xs font-semibold bg-accent text-accent-foreground disabled:opacity-40 transition-colors">Create</button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1">
        {/* All items chip */}
        <button
          onClick={() => onSelectFolder(null)}
          className={`shrink-0 flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-all duration-150 ${
            activeFolderId === null
              ? 'bg-accent text-accent-foreground border-accent'
              : 'border-border text-muted-foreground hover:border-accent/40 hover:text-foreground'
          }`}
        >
          <Bookmark size={11} />
          All ({totalCount})
        </button>

        {folders.map((folder) => {
          const count = (folderItems[folder.id] || []).length;
          const isActive = activeFolderId === folder.id;
          return (
            <div key={folder.id} className="shrink-0 flex items-center gap-0.5">
              <button
                onClick={() => onSelectFolder(isActive ? null : folder.id)}
                className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-all duration-150 ${
                  isActive
                    ? 'text-white border-transparent' :'border-border text-muted-foreground hover:border-accent/40 hover:text-foreground'
                }`}
                style={isActive ? { background: folder.color, borderColor: folder.color } : {}}
              >
                <Folder size={11} style={{ color: isActive ? 'white' : folder.color }} />
                {folder.name} ({count})
              </button>
              <button
                onClick={() => onDeleteFolder(folder.id)}
                className="p-1 rounded-full text-muted-foreground hover:text-red-400 hover:bg-red-400/10 transition-all duration-150"
                aria-label={`Delete ${folder.name} collection`}
              >
                <X size={10} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

function SavedPassagesInner() {
  const [passageItems, setPassageItems] = useState<LocalPassage[]>([]);
  const [aiItems, setAIItems] = useState<LocalAISavedItem[]>([]);
  const [activeTypeFilter, setActiveTypeFilter] = useState<TypeFilter>('all');
  const [activeSourceFilter, setActiveSourceFilter] = useState('all');
  const [sortOption, setSortOption] = useState<SortOption>('date-desc');
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [folders, setFolders] = useState<CollectionFolder[]>([]);
  const [folderItems, setFolderItems] = useState<Record<string, string[]>>({});
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  const [folderModalItem, setFolderModalItem] = useState<DisplayItem | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const supabase = createClient();

  useEffect(() => { setMounted(true); }, []);

  // Load folders from localStorage
  useEffect(() => {
    if (!mounted) return;
    setFolders(loadLocalFolders());
    setFolderItems(loadLocalFolderItems());
  }, [mounted]);

  // Load items from Supabase or localStorage
  useEffect(() => {
    if (!mounted) return;

    async function loadItems() {
      try {
        const { data: { user } } = await supabase.auth.getUser();

        if (user) {
          setIsLoggedIn(true);

          // Always load localStorage passages for logged-in users (passages may have been
          // saved locally before sign-in or if Supabase insert failed silently)
          let localPassages: LocalPassage[] = [];
          try { localPassages = loadLocalPassages(); } catch { localPassages = []; }

          const { data: passageData, error: passageError } = await supabase
            .from('saved_items')
            .select('id, content, title, book, reference, source, created_at')
            .eq('user_id', user.id)
            .eq('item_type', 'passage')
            .order('created_at', { ascending: false });

          if (passageError) {
            console.error('Error loading passages from Supabase:', passageError);
          }

          const supabasePassages: LocalPassage[] = (passageData || []).map((row) => ({
            id: row.id,
            book: normalizeBookType(row.book),
            title: row.title || '',
            snippet: row.content,
            reference: row.reference || undefined,
            savedAt: new Date(row.created_at).getTime(),
            source: row.source || 'Search',
          }));

          // Merge: use Supabase passages as primary, append local passages not already in Supabase
          // De-duplicate by snippet content to avoid showing the same passage twice
          const supabaseSnippets = new Set(supabasePassages.map((p) => p.snippet));
          const uniqueLocalPassages = localPassages.filter((p) => !supabaseSnippets.has(p.snippet));
          setPassageItems([...supabasePassages, ...uniqueLocalPassages]);

          const { data: aiData, error: aiError } = await supabase
            .from('saved_items')
            .select('id, content, question, source, created_at')
            .eq('user_id', user.id)
            .eq('item_type', 'ai')
            .order('created_at', { ascending: false });

          if (aiError) {
            console.error('Error loading AI answers from Supabase:', aiError);
          }

          // Also merge localStorage AI answers for logged-in users
          let localAI: LocalAISavedItem[] = [];
          try { localAI = loadLocalAISaved(); } catch { localAI = []; }

          const supabaseAI: LocalAISavedItem[] = (aiData || []).map((row) => ({
            id: row.id,
            question: row.question || '',
            answer: row.content,
            date: row.created_at,
            source: row.source || 'AI Chat',
          }));

          // De-duplicate AI answers by answer content
          const supabaseAIAnswers = new Set(supabaseAI.map((a) => a.answer));
          const uniqueLocalAI = localAI.filter((a) => !supabaseAIAnswers.has(a.answer));
          setAIItems([...supabaseAI, ...uniqueLocalAI]);
        } else {
          setIsLoggedIn(false);
          try { setPassageItems(loadLocalPassages()); } catch { setPassageItems([]); }
          try { setAIItems(loadLocalAISaved()); } catch { setAIItems([]); }
        }
      } catch (error) {
        console.error('Failed to load saved items:', error);
        try { setPassageItems(loadLocalPassages()); } catch { setPassageItems([]); }
        try { setAIItems(loadLocalAISaved()); } catch { setAIItems([]); }
      }
    }

    loadItems();
  }, [mounted]);

  // Build unified items
  const allItems: DisplayItem[] = (() => {
    try {
      const passages = (passageItems || []).map((item) => ({
        id: item.id || String(Math.random()),
        type: 'passage' as const,
        book: item.book,
        title: item.title || undefined,
        snippet: item.snippet || '',
        reference: item.reference,
        savedAt: typeof item.savedAt === 'number' && !isNaN(item.savedAt) ? item.savedAt : Date.now(),
        source: item.source || 'Search',
      }));
      const aiAnswers = (aiItems || []).map((item) => ({
        id: item.id || String(Math.random()),
        type: 'ai' as const,
        question: item.question || '',
        answer: item.answer || '',
        savedAt: safeDateToTimestamp(item.date),
        source: item.source || 'AI Chat',
      }));
      return [...passages, ...aiAnswers];
    } catch { return []; }
  })();

  // Apply folder filter
  const folderFiltered = activeFolderId
    ? allItems.filter((item) => (folderItems[activeFolderId] || []).includes(item.id))
    : allItems;

  // Apply type filter
  const typeFiltered = (() => {
    if (activeTypeFilter === 'passage') return folderFiltered.filter((i) => i.type === 'passage');
    if (activeTypeFilter === 'ai') return folderFiltered.filter((i) => i.type === 'ai');
    return folderFiltered;
  })();

  // Apply source filter (only relevant for passages)
  const sourceFiltered = (() => {
    if (activeSourceFilter === 'all') return typeFiltered;
    return typeFiltered.filter((item) => item.type === 'passage' && item.book === activeSourceFilter);
  })();

  // Apply sort
  const sorted = [...sourceFiltered].sort((a, b) => {
    switch (sortOption) {
      case 'date-desc': return b.savedAt - a.savedAt;
      case 'date-asc': return a.savedAt - b.savedAt;
      case 'source-asc': return a.source.localeCompare(b.source);
      case 'source-desc': return b.source.localeCompare(a.source);
      default: return b.savedAt - a.savedAt;
    }
  });

  const totalCount = allItems.length;
  const hasActiveFilter = activeTypeFilter !== 'all' || activeSourceFilter !== 'all' || activeFolderId !== null;

  // ─── Folder management ───

  const handleCreateFolder = useCallback((name: string, color: string) => {
    const newFolder: CollectionFolder = {
      id: `folder-${Date.now()}`,
      name,
      color,
      createdAt: Date.now(),
    };
    setFolders((prev) => {
      const updated = [...prev, newFolder];
      saveLocalFolders(updated);
      return updated;
    });
    toast.success(`Collection "${name}" created`);
  }, []);

  const handleDeleteFolder = useCallback((folderId: string) => {
    setFolders((prev) => {
      const updated = prev.filter((f) => f.id !== folderId);
      saveLocalFolders(updated);
      return updated;
    });
    setFolderItems((prev) => {
      const updated = { ...prev };
      delete updated[folderId];
      saveLocalFolderItems(updated);
      return updated;
    });
    if (activeFolderId === folderId) setActiveFolderId(null);
    toast.success('Collection deleted');
  }, [activeFolderId]);

  const handleAddToFolder = useCallback((folderId: string, itemId: string) => {
    setFolderItems((prev) => {
      const existing = prev[folderId] || [];
      if (existing.includes(itemId)) return prev;
      const updated = { ...prev, [folderId]: [...existing, itemId] };
      saveLocalFolderItems(updated);
      return updated;
    });
    const folder = folders.find((f) => f.id === folderId);
    toast.success(`Added to "${folder?.name || 'collection'}"`);
  }, [folders]);

  const handleRemoveFromFolder = useCallback((folderId: string, itemId: string) => {
    setFolderItems((prev) => {
      const updated = { ...prev, [folderId]: (prev[folderId] || []).filter((id) => id !== itemId) };
      saveLocalFolderItems(updated);
      return updated;
    });
    const folder = folders.find((f) => f.id === folderId);
    toast.success(`Removed from "${folder?.name || 'collection'}"`);
  }, [folders]);

  // ─── Item management ───

  const handleDelete = useCallback(async (item: DisplayItem) => {
    try {
      if (isLoggedIn) {
        const { error } = await supabase.from('saved_items').delete().eq('id', item.id);
        if (error) throw error;
      }
      if (item.type === 'passage') {
        setPassageItems((prev) => {
          const updated = (prev || []).filter((p) => p.id !== item.id);
          // Remove from localStorage by both ID and snippet content so that
          // local copies of the same passage don't resurface via the merge logic
          const localUpdated = loadLocalPassages().filter(
            (p) => p.id !== item.id && p.snippet !== item.snippet
          );
          saveLocalPassages(localUpdated);
          return updated;
        });
      } else {
        setAIItems((prev) => {
          const updated = (prev || []).filter((a) => a.id !== item.id);
          // Remove from localStorage by both ID and answer content
          const localUpdated = loadLocalAISaved().filter(
            (a) => a.id !== item.id && a.answer !== item.answer
          );
          saveLocalAISaved(localUpdated);
          return updated;
        });
      }
      // Remove from all folders
      setFolderItems((prev) => {
        const updated: Record<string, string[]> = {};
        for (const [fid, ids] of Object.entries(prev)) {
          updated[fid] = ids.filter((id) => id !== item.id);
        }
        saveLocalFolderItems(updated);
        return updated;
      });
      toast.success('Removed from library');
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Could not remove item');
    }
  }, [isLoggedIn]);

  const handleClearAll = useCallback(async () => {
    try {
      if (isLoggedIn) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) await supabase.from('saved_items').delete().eq('user_id', user.id);
      }
      // Always clear localStorage so items don't reappear after navigation
      saveLocalPassages([]);
      saveLocalAISaved([]);
      setPassageItems([]);
      setAIItems([]);
      setFolderItems({});
      saveLocalFolderItems({});
      setShowClearConfirm(false);
      toast.success('All saved items cleared');
    } catch (error) {
      console.error('Error clearing items:', error);
      setPassageItems([]);
      setAIItems([]);
      setShowClearConfirm(false);
    }
  }, [isLoggedIn]);

  if (!mounted) {
    return (
      <div className="max-w-lg mx-auto px-4 pt-12">
        <div className="skeleton-shimmer h-8 w-40 rounded-xl mb-6" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={`sk-${i}`} className="rounded-2xl border border-border bg-card p-4 h-32 skeleton-shimmer" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4">
      {/* Header */}
      <div className="flex items-center gap-3 pt-12 pb-4">
        <Link href="/" className="p-2 rounded-xl hover:bg-muted transition-colors" aria-label="Back to home">
          <ChevronLeft size={20} className="text-muted-foreground" />
        </Link>
        <div className="flex-1">
          <h1 className="text-lg font-bold text-foreground">Saved</h1>
          <p className="text-xs text-muted-foreground">
            {totalCount === 0 ? 'No saved items yet' : `${totalCount} item${totalCount !== 1 ? 's' : ''} saved`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {totalCount > 0 && (
            <button
              onClick={() => setShowFilters((v) => !v)}
              className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-xl border transition-all duration-150 ${
                showFilters || hasActiveFilter
                  ? 'bg-accent/15 border-accent/40 text-accent' :'border-border text-muted-foreground hover:border-accent/40 hover:text-foreground'
              }`}
            >
              <Filter size={12} />
              Filter
              {hasActiveFilter && <span className="w-1.5 h-1.5 rounded-full bg-accent" />}
            </button>
          )}
          {totalCount > 0 && (
            <button
              onClick={() => setShowClearConfirm(true)}
              className="flex items-center gap-1.5 text-xs font-semibold text-red-400 hover:text-red-300 px-3 py-1.5 rounded-xl hover:bg-red-400/10 transition-all duration-150"
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Collections panel */}
      {totalCount > 0 && (
        <FoldersPanel
          folders={folders}
          folderItems={folderItems}
          activeFolderId={activeFolderId}
          onSelectFolder={setActiveFolderId}
          onDeleteFolder={handleDeleteFolder}
          onCreateFolder={handleCreateFolder}
          totalCount={totalCount}
        />
      )}

      {/* Filter & Sort panel */}
      {showFilters && totalCount > 0 && (
        <div className="rounded-2xl border border-border bg-card/50 p-4 mb-4 space-y-4">
          {/* Type filter */}
          <div>
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide mb-2">Type</p>
            <div className="flex gap-2">
              {([
                { value: 'all', label: 'All' },
                { value: 'passage', label: 'Passages' },
                { value: 'ai', label: 'AI Answers' },
              ] as { value: TypeFilter; label: string }[]).map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setActiveTypeFilter(value)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all duration-150 ${
                    activeTypeFilter === value
                      ? 'bg-accent text-accent-foreground border-accent'
                      : 'border-border text-muted-foreground hover:border-accent/40 hover:text-foreground'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Source filter (only for passages) */}
          {activeTypeFilter !== 'ai' && (
            <div>
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide mb-2">Source</p>
              <div className="flex gap-2 flex-wrap">
                {BOOK_FILTER_OPTIONS.map(({ key, label, value }) => (
                  <button
                    key={key}
                    onClick={() => setActiveSourceFilter(value)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all duration-150 ${
                      activeSourceFilter === value
                        ? 'bg-accent text-accent-foreground border-accent'
                        : 'border-border text-muted-foreground hover:border-accent/40 hover:text-foreground'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sort */}
          <div>
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide mb-2">Sort by</p>
            <div className="flex gap-2 flex-wrap">
              {SORT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSortOption(opt.value)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all duration-150 ${
                    sortOption === opt.value
                      ? 'bg-accent text-accent-foreground border-accent'
                      : 'border-border text-muted-foreground hover:border-accent/40 hover:text-foreground'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Reset */}
          {hasActiveFilter && (
            <button
              onClick={() => { setActiveTypeFilter('all'); setActiveSourceFilter('all'); setActiveFolderId(null); }}
              className="text-xs font-semibold text-red-400 hover:text-red-300 transition-colors"
            >
              Reset filters
            </button>
          )}
        </div>
      )}

      {/* Results count */}
      {totalCount > 0 && hasActiveFilter && (
        <p className="text-xs text-muted-foreground mb-3">
          <span className="text-foreground font-semibold">{sorted.length}</span> item{sorted.length !== 1 ? 's' : ''} shown
        </p>
      )}

      {/* Sort quick access (when filters hidden) */}
      {totalCount > 0 && !showFilters && (
        <div className="flex items-center justify-end mb-3">
          <SortDropdown value={sortOption} onChange={setSortOption} />
        </div>
      )}

      {/* Item list or empty state */}
      {sorted.length === 0 ? (
        <EmptyState hasFilter={hasActiveFilter} />
      ) : (
        <div className="space-y-3 pb-24">
          {sorted.map((item, i) =>
            item.type === 'ai' ? (
              <AIAnswerCard
                key={item.id}
                item={item}
                onDelete={() => handleDelete(item)}
                onAddToFolder={setFolderModalItem}
                animationDelay={i * 50}
              />
            ) : (
              <PassageCard
                key={item.id}
                item={item}
                onDelete={() => handleDelete(item)}
                onAddToFolder={setFolderModalItem}
                animationDelay={i * 50}
              />
            )
          )}
        </div>
      )}

      {/* Add to folder modal */}
      {folderModalItem && (
        <AddToFolderModal
          item={folderModalItem}
          folders={folders}
          folderItems={folderItems}
          onClose={() => setFolderModalItem(null)}
          onAddToFolder={(folderId, itemId) => { handleAddToFolder(folderId, itemId); }}
          onRemoveFromFolder={(folderId, itemId) => { handleRemoveFromFolder(folderId, itemId); }}
          onCreateFolder={(name, color) => { handleCreateFolder(name, color); }}
        />
      )}

      {/* Clear all confirmation modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 z-50 flex items-end justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl p-5 animate-fade-in-up" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-red-400/10 flex items-center justify-center">
                <Trash2 size={18} className="text-red-400" />
              </div>
              <h3 className="text-base font-bold text-foreground">Clear all saved items?</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              This will permanently delete all {totalCount} saved item{totalCount !== 1 ? 's' : ''}. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowClearConfirm(false)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all duration-150"
                style={{ background: 'var(--muted)', color: 'var(--foreground)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleClearAll}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-red-500 text-white hover:bg-red-600 transition-all duration-150"
              >
                Clear all
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SavedPassagesContent() {
  return (
    <SavedTabErrorBoundary>
      <SavedPassagesInner />
    </SavedTabErrorBoundary>
  );
}
